import { CalendarsFilters } from '@modules/calendar-app/types/calendars-filters';
import { CalendarsListState } from '@modules/calendar-app/types/calendars-list-state';
import { EventsFilters } from '@modules/calendar-app/types/events-filters';
import { EventsListState } from '@modules/calendar-app/types/events-list-state';
import { type StitchSearchConfig } from '@modules/common/services/base-stitch.service';
import { Application } from '@modules/common/types/application';
import { SortOrder } from '@modules/common/types/sort-order';
import { type Stitch } from '@modules/common/types/stitch';
import { type StitchFilters } from '@modules/common/types/stitch-filters';
import { mergeMarkers } from '@modules/common/utils/merge-markers';
import { type Connection } from '@modules/connections/types/connection';
import { ContactsFilters } from '@modules/contacts/types/contacts-filters';
import { ContactsListState } from '@modules/contacts/types/contacts-list-state';
import { GroupsFilters } from '@modules/contacts/types/groups-filters';
import { GroupsListState } from '@modules/contacts/types/groups-list-state';
import { FilesFilters } from '@modules/files/types/files-filters';
import { FilesListState } from '@modules/files/types/files-list-state';
import { FoldersFilters } from '@modules/files/types/folders-filters';
import { FoldersListState } from '@modules/files/types/folders-list-state';
import { KPListType } from '@modules/knowledge/types/kp-list-type';
import { getKPResultType, type KPResultListType } from '@modules/knowledge/types/kp-result-types';
import { type Knot } from '@modules/knots/types/knot';
import { MessageFoldersFilters } from '@modules/messages/types/message-folders-filters';
import { MessageFoldersListState } from '@modules/messages/types/message-folders-list-state';
import { MessagesFilters } from '@modules/messages/types/messages-filters';
import { MessagesListState } from '@modules/messages/types/messages-list-state';
import { NotebooksFilters } from '@modules/notes/types/notebooks-filters';
import { NotebooksListState } from '@modules/notes/types/notebooks-list-state';
import { NotesFilters } from '@modules/notes/types/notes-filters';
import { NotesListState } from '@modules/notes/types/notes-list-state';
import { type AdvancedSearchState } from '@modules/search/types/advanced-search-state';
import { SheetFoldersFilters } from '@modules/sheets/types/sheet-folders-filters';
import { SheetFoldersListState } from '@modules/sheets/types/sheet-folders-list-state';
import { SheetsFilters } from '@modules/sheets/types/sheets-filters';
import { SheetsListState } from '@modules/sheets/types/sheets-list-state';
import { type Tag } from '@modules/tags/types/tag';
import { FormStitchFilters } from '@modules/forms/services/forms.service';
import { FormFolderStitchFilters } from '@modules/forms/services/form-folders.service';
import { FormsKpListState } from '@modules/forms/types/forms-kp-list-state';
import { ProjectsFilters } from '@modules/tasks/types/projects-filters';
import { ProjectsListState } from '@modules/tasks/types/projects-list-state';
import { TasksFilters } from '@modules/tasks/types/tasks-filters';
import { TasksListState } from '@modules/tasks/types/tasks-list-state';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';

export interface KPResultsQuery {
  readonly tags: Tag[];
  readonly knots: Knot[];
  readonly connections: Connection[];
  readonly search: AdvancedSearchState | null;
  /** Free-text keywords picked in the KP search; each one narrows the results. */
  readonly keywords?: readonly string[];
  /** The item open in the 3rd panel; it never lists itself among its own results. */
  readonly contextItem: Stitch | null | undefined;
  /**
   * `buildKPResultsFilters` reads it only when a date range is applied, so a signal passed there is tracked
   * only then. `buildKPListRequest` reads it on every call.
   */
  readonly timeZone: () => string;
}

export interface KPListPage {
  readonly limit: number;
  readonly offset: number;
}

export type KPResultsFilters = Pick<
  StitchFilters<StitchItemSummaryDto>,
  | 'query'
  | 'query_boost'
  | 'tags'
  | 'tags_boost'
  | 'knots'
  | 'knots_boost'
  | 'connections'
  | 'connections_boost'
  | 'mustQuery'
  | 'exactTimeRange'
  | 'fromTime'
  | 'toTime'
  | 'displayTimeZone'
  | 'withEmpty'
  | 'noParent'
  | 'exceptIds'
  | 'archived'
  | 'deleted'
  | 'hubMode'
  | 'limit'
  | 'offset'
  | 'short'
>;

/**
 * The KP search applied to a section's own filters: what a KP result list of `type` asks for. Returns a
 * copy; `filters` is left untouched.
 */
export function buildKPResultsFilters<F extends KPResultsFilters>(
  filters: F,
  type: KPListType,
  query: KPResultsQuery,
): F {
  const resultsFilters = { ...filters };
  resultsFilters.query_boost = 0.1;
  resultsFilters.tags_boost = 20;
  resultsFilters.knots_boost = 15;
  resultsFilters.connections_boost = 10;

  // Knowledge markers (left-panel selection) and the KP advanced-search knowledge items both drive
  // the results: merge each kind (de-duplicated by name) so a search narrows/augments on top of markers.
  const { search } = query;
  const mergedTags = mergeMarkers(query.tags, search?.tags);
  const mergedKnots = mergeMarkers(query.knots, search?.knots);
  const mergedConnections = mergeMarkers(query.connections, search?.connections);

  const markerNames: string[] = [];
  resultsFilters.query = markerNames;

  if (mergedTags.length) {
    resultsFilters.tags = mergedTags;
    markerNames.push(...mergedTags.map((i) => i.name));
  }
  if (mergedKnots.length) {
    resultsFilters.knots = mergedKnots;
    markerNames.push(...mergedKnots.map((i) => i.name));
  }
  if (mergedConnections.length) {
    resultsFilters.connections = mergedConnections;
    markerNames.push(...mergedConnections.map((i) => i.name));
  }

  // Free-text query from the KP search field ANDs on top of the marker-driven results: it goes to
  // `mustQuery` (backend `must` clause), NOT into `query[]` — the backend ORs `query[]` entries
  // (`should` + `minimum_should_match: 1`), which would widen the result set instead of narrowing it.
  // `must_query` takes one string: the keywords join it and narrow together.
  const freeText = [search?.query, ...(query.keywords ?? [])]
    .map((text) => text?.trim())
    .filter(Boolean)
    .join(' ');
  resultsFilters.mustQuery = freeText || undefined;

  // KP advanced-form filters (date range) narrow the results too. Mirrors applyAdvancedFilters:
  // the search range replaces any list-state day window, and picker values are bare days that
  // still need widening to account-zone day bounds — so the day window's `exactTimeRange` flag
  // must not survive onto them (it would send the raw picker instants, dropping most of the
  // final day; see StitchFilters.rangeStart/rangeEnd).
  const kpFilters = search?.[Application.kp];
  const hasDateRange = !!(kpFilters?.dateRangeFrom || kpFilters?.dateRangeTo);
  if (hasDateRange) {
    resultsFilters.exactTimeRange = false;
    resultsFilters.fromTime = kpFilters.dateRangeFrom ?? undefined;
    resultsFilters.toTime = kpFilters.dateRangeTo ?? undefined;
    // Re-read the zone at search time: `filters` captured it when they were built, which first happens
    // during init — before space settings may have resolved the account zone. The range widening above
    // is exactly where a stale browser zone would shift the day bounds.
    resultsFilters.displayTimeZone = query.timeZone();
  }

  // Skip the request only when NOTHING filters the results — markers, free text and the date range
  // all count. `query.length` alone would short-circuit a date-only search into zero results.
  resultsFilters.withEmpty = markerNames.length === 0 && !freeText && !hasDateRange;

  // A KP result list is a SEARCH surface, never a folder browse, so it must span every container:
  // a task inside a project has to surface beside an unfiled one. The backend already drops its
  // orphan-only default when it can see a search (`isSearchApplied` — text or markers), but a
  // DATE-ONLY search carries neither, and the tasks list otherwise forces `no_parent` on every
  // request. Declaring the scope here covers all three shapes, and only for an actual search:
  // with nothing applied the request is short-circuited by `withEmpty` above anyway.
  //
  // For TASKS this also lets a matching SUBTASK surface as its own row, and that is deliberate: a
  // search should find what matches regardless of nesting, as it does for the other thirteen result
  // types. It is not separable from here either — `no_parent` names two different things on the
  // two paths (ES `search.service.ts` maps `no_parent: query.no_parent ?? query.no_project` for
  // TasksSearchQuery, i.e. "parent" IS the container; the tasking-app DB path reads it as
  // "top-level only"), and on the DB path `false` is indistinguishable from unset. Pulling the
  // container lever instead therefore builds the identical request. Restricting KP results to
  // top-level tasks would need the backend to stop conflating the two for tasks.
  resultsFilters.noParent = resultsFilters.withEmpty ? undefined : false;

  const stitchType = getKPResultType(type)?.stitchType;
  const { contextItem } = query;
  if (stitchType && contextItem?.getStitchType() === stitchType) {
    resultsFilters.exceptIds = [contextItem.id];
  }

  return resultsFilters;
}

const KP_RESULTS_SORT = { by: 'score', order: SortOrder.DESC } as const;

interface KPListDefaults {
  /** The section's filters for its list state as created: nothing picked in its sort menu. */
  readonly filters: (displayTimeZone: string) => KPResultsFilters;
  /** What the type's list component adds to its filters in `getItems`. */
  readonly request: (filters: KPResultsFilters, page: KPListPage) => KPResultsFilters;
}

const activeItemsPage = (filters: KPResultsFilters, page: KPListPage): KPResultsFilters => ({
  ...filters,
  archived: filters.archived ?? false,
  deleted: filters.deleted ?? false,
  limit: page.limit,
  offset: page.offset,
});

const itemsPage = (filters: KPResultsFilters, page: KPListPage): KPResultsFilters => ({
  ...filters,
  limit: page.limit,
  offset: page.offset,
});
const formsPage = (filters: KPResultsFilters, page: KPListPage): KPResultsFilters => ({ ...activeItemsPage(filters, page), short: true });

const KP_LIST_DEFAULTS: Record<KPResultListType, KPListDefaults> = {
  [KPListType.form]: {
    filters: (displayTimeZone) => new FormStitchFilters({ displayTimeZone }).applyListState(new FormsKpListState(KP_RESULTS_SORT)),
    request: formsPage,
  },
  [KPListType.formFolder]: {
    filters: (displayTimeZone) => new FormFolderStitchFilters({ displayTimeZone }).applyListState(new FormsKpListState(KP_RESULTS_SORT)),
    request: formsPage,
  },
  [KPListType.message]: {
    filters: (displayTimeZone) => {
      const filters = new MessagesFilters({ displayTimeZone }).applyListState(
        new MessagesListState({ defaultSort: KP_RESULTS_SORT }),
      );
      filters.threadedView = false;
      return filters;
    },
    request: (filters, page) => ({ ...activeItemsPage(filters, page), short: true }),
  },
  [KPListType.event]: {
    filters: (displayTimeZone) =>
      new EventsFilters({ displayTimeZone }).applyListState(new EventsListState({ defaultSort: KP_RESULTS_SORT })),
    request: activeItemsPage,
  },
  [KPListType.task]: {
    filters: (displayTimeZone) =>
      new TasksFilters({ displayTimeZone }).applyListState(new TasksListState({ defaultSort: KP_RESULTS_SORT })),
    request: (filters, page) => ({
      ...activeItemsPage(filters, page),
      noParent: filters.hubMode ? undefined : (filters.noParent ?? true),
    }),
  },
  [KPListType.note]: {
    filters: (displayTimeZone) =>
      new NotesFilters({ displayTimeZone }).applyListState(new NotesListState({ defaultSort: KP_RESULTS_SORT })),
    request: activeItemsPage,
  },
  [KPListType.contact]: {
    filters: (displayTimeZone) =>
      new ContactsFilters({ displayTimeZone }).applyListState(new ContactsListState({ defaultSort: KP_RESULTS_SORT })),
    request: activeItemsPage,
  },
  [KPListType.file]: {
    filters: (displayTimeZone) =>
      new FilesFilters({ displayTimeZone }).applyListState(new FilesListState({ defaultSort: KP_RESULTS_SORT })),
    request: activeItemsPage,
  },
  [KPListType.sheet]: {
    filters: (displayTimeZone) =>
      new SheetsFilters({ displayTimeZone }).applyListState(new SheetsListState({ defaultSort: KP_RESULTS_SORT })),
    request: activeItemsPage,
  },
  [KPListType.messageFolder]: {
    // No KP sort here: MessageFolderListStateService does not pass its configured `defaultSort` on, so the
    // section's list state starts on the folders' own default sort.
    filters: (displayTimeZone) =>
      new MessageFoldersFilters({ displayTimeZone }).applyListState(new MessageFoldersListState({})),
    request: itemsPage,
  },
  [KPListType.calendar]: {
    filters: (displayTimeZone) =>
      new CalendarsFilters({ displayTimeZone }).applyListState(
        new CalendarsListState({ defaultSort: KP_RESULTS_SORT }),
      ),
    request: activeItemsPage,
  },
  [KPListType.project]: {
    filters: (displayTimeZone) =>
      new ProjectsFilters({ displayTimeZone }).applyListState(new ProjectsListState({ defaultSort: KP_RESULTS_SORT })),
    request: itemsPage,
  },
  [KPListType.notebook]: {
    filters: (displayTimeZone) =>
      new NotebooksFilters({ displayTimeZone }).applyListState(
        new NotebooksListState({ defaultSort: KP_RESULTS_SORT }),
      ),
    request: itemsPage,
  },
  [KPListType.group]: {
    filters: (displayTimeZone) =>
      new GroupsFilters({ displayTimeZone }).applyListState(new GroupsListState({ defaultSort: KP_RESULTS_SORT })),
    request: itemsPage,
  },
  [KPListType.folder]: {
    filters: (displayTimeZone) =>
      new FoldersFilters({ displayTimeZone }).applyListState(new FoldersListState({ defaultSort: KP_RESULTS_SORT })),
    request: itemsPage,
  },
  [KPListType.sheetFolder]: {
    filters: (displayTimeZone) =>
      new SheetFoldersFilters({ displayTimeZone }).applyListState(
        new SheetFoldersListState({ defaultSort: KP_RESULTS_SORT }),
      ),
    request: itemsPage,
  },
};

/**
 * The request the KP result list of `type` sends for `query` and `page`, as that list is first shown:
 * its section's sort menu untouched. A type's list state is dropped whenever another type is picked,
 * so this is also exactly what the list sends once `type` is picked.
 */
export function buildKPListRequest(type: KPResultListType, query: KPResultsQuery, page: KPListPage): KPResultsFilters {
  const defaults = KP_LIST_DEFAULTS[type];
  return defaults.request(buildKPResultsFilters(defaults.filters(query.timeZone()), type, query), page);
}

/**
 * The tenant source a KP result list is fed: the item open in the 3rd panel, so a member workspace's item
 * lists its related rows from that item's owner. Empty while nothing is open. The sections bind it to their
 * lists and the type menu's counts use it too, so a badge counts the tenant its list pages through.
 */
export function kpContextTenantItems(contextItem: Stitch | null | undefined): ReadonlyArray<Stitch> {
  return contextItem ? [contextItem] : [];
}

/** The result lists whose `search` carries {@link kpContextTenantItems}; the others search without it. */
const KP_TENANT_SCOPED_LIST_TYPES: ReadonlySet<KPListType> = new Set<KPListType>([
  KPListType.contact,
  KPListType.group,
  KPListType.task,
  KPListType.project,
  KPListType.message,
  KPListType.event,
  KPListType.calendar,
  KPListType.note,
  KPListType.notebook,
  KPListType.form,
  KPListType.formFolder,
]);

/** The search config a KP result list of `type` sends for `contextItem`, as far as the tenant goes. */
export function kpListSearchConfig(
  type: KPListType,
  contextItem: Stitch | null | undefined,
): Pick<StitchSearchConfig, 'tenantItems'> | undefined {
  return KP_TENANT_SCOPED_LIST_TYPES.has(type) ? { tenantItems: kpContextTenantItems(contextItem) } : undefined;
}
